import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import ImageIcon from "@mui/icons-material/Image";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme, alpha } from "@mui/material";
import { ChatMessage } from "../hooks/useSessionPolling";
import { KnownIssueMatch } from "../utils/backend-utils";
import { ImageViewer } from "./ImageViewer";
import ReactMarkdown from 'react-markdown'

interface ChatMessagesProps {
    messages: ChatMessage[];
    onSendReply?: (message: string) => void;
    highlightedSourceTimestamp?: string | null;
}

// Parse suggested actions from bot message
function parseSuggestedActions(text: string): string[] {
    // Look for pattern like "e.g., 'Action1', 'Action2', or 'Action3'"
    // Extract all quoted strings after "e.g.,"
    const egMatch = text.match(/e\.g\.,\s*(.+?)(?:\)|$)/i);
    if (!egMatch) return [];
    
    const egText = egMatch[1];
    const actions: string[] = [];
    
    // Match all quoted strings using exec loop (compatible with older targets)
    const quotedRegex = /'([^']+)'/g;
    let match;
    while ((match = quotedRegex.exec(egText)) !== null) {
        if (match[1]) {
            actions.push(match[1].trim());
        }
    }
    
    return actions;
}

// Strip internal key markers (e.g. " (key=os_family)") from rendered
// messages. These are only there for the backend's regex-based fact
// extraction — they must never be visible to the user. Applied to every
// rendered message so both fresh submissions and restored sessions get the
// cleaner output.
function stripInternalKeys(text: string): string {
    if (!text) return text;
    return text.replace(/\s*\(key=[a-z0-9_]+\)/gi, '');
}

// Check if message is asking for user action after completion
function isCompletionQuestion(text: string): boolean {
    if (!text) return false; // Guard against undefined/empty text
    return text.toLowerCase().includes('all execution steps are complete') && 
           text.toLowerCase().includes('what would you like to do');
}

function CollapsibleMessage({ msg, onSendReply }: { msg: ChatMessage; onSendReply?: (message: string) => void }) {
    const theme = useTheme();
    const contentRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [needsExpansion, setNeedsExpansion] = useState(false);
    const [clickedAction, setClickedAction] = useState<string | null>(null);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string>("");
    const [attachmentUrls, setAttachmentUrls] = useState<{ url: string; isLocal: boolean }[]>([]);
    const maxHeightVh = 30; // 30% of viewport height

    // Check if this is a completion question with suggested actions
    const isCompletion = msg.isBot && isCompletionQuestion(msg.text);
    const suggestedActions = isCompletion ? parseSuggestedActions(msg.text) : [];

    // Prepare attachment URLs (either from signed URLs or local files)

    useEffect(() => {
        const urls: { url: string; isLocal: boolean }[] = [];
        const blobUrls: string[] = [];
        
        if (msg.signedAttachments) {
            urls.push(...msg.signedAttachments.map(url => ({ url, isLocal: false })));
        }
        
        if (msg.localFiles) {
            msg.localFiles.forEach(file => {
                const blobUrl = URL.createObjectURL(file);
                blobUrls.push(blobUrl);
                urls.push({ url: blobUrl, isLocal: true });
            });
        }

        setAttachmentUrls(urls);

        // Cleanup blob URLs on unmount
        return () => {
            blobUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [msg.signedAttachments, msg.localFiles]);

    const handleImageClick = (url: string) => {
        setSelectedImage(url);
        setImageViewerOpen(true);
    };

    // Check if content overflows the max height
    useEffect(() => {
        const checkOverflow = () => {
            const maxHeight = (window.innerHeight * maxHeightVh) / 100;
            const contentHeight = contentRef.current?.scrollHeight || 0;
            setNeedsExpansion(contentHeight > maxHeight);
        };
        
        // Check on mount and when content changes
        checkOverflow();
        
        // Also check on window resize
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [msg.text]);

    // Skip rendering plan placeholder messages (they're handled separately)
    // Note: This is after all hooks to comply with rules of hooks
    if (msg.planPlaceholder) {
        return null;
    }

    // Completion CTA: dedicated card that opens the structured report.
    // Replaces the old "dump the final message into a chat bubble"
    // behaviour so the deliverable feels like documentation, not chat.
    if (msg.messageType === 'report_cta' && msg.reportSessionId) {
        return (
            <ReportCtaCard
                sessionId={msg.reportSessionId}
                isCustomerTemplate={msg.reportIsCustomerTemplate}
                reportKind={msg.reportKind}
            />
        );
    }

    // Known-issue diagnostic card: deterministic regex hits from
    // known_issues.json on the user's last reply. Rendered inline with the
    // bot's evaluation so the operator sees the named issue, its cause,
    // and the documented resolution next to the LLM's grading message.
    if (msg.messageType === 'known_issue' && msg.knownIssueMatches?.length) {
        return <KnownIssueCard matches={msg.knownIssueMatches} />;
    }

    const handleActionClick = (action: string) => {
        console.log('[ChatMessages] Button clicked:', action);
        setClickedAction(action);
        if (onSendReply) {
            console.log('[ChatMessages] Calling onSendReply with:', action);
            onSendReply(action);
        } else {
            console.error('[ChatMessages] onSendReply is not defined!');
        }
    };

    return (
        <Box
            data-message-ts={msg.sourceTimestamp}
            sx={{
                alignSelf: msg.isBot ? 'flex-start' : 'flex-end',
                maxWidth: '85%',
                outline: msg.highlight ? (theme) => `2px solid ${alpha(theme.palette.warning.main, 0.8)}` : 'none',
                borderRadius: 2,
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    borderRadius: msg.isBot
                        ? '16px 16px 16px 4px'
                        : '16px 16px 4px 16px',
                    bgcolor: msg.isBot
                        ? alpha(theme.palette.success.light, 0.15)
                        : alpha(theme.palette.primary.light, 0.15),
                    border: `1px solid ${alpha(
                        msg.isBot
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                        0.2
                    )}`,
                    overflow: 'hidden',
                }}
            >
                <Box
                    ref={contentRef}
                    sx={{
                        p: 2,
                        maxHeight: needsExpansion && !isExpanded ? `${maxHeightVh}vh` : 'none',
                        overflow: needsExpansion && !isExpanded ? 'hidden' : 'visible',
                        transition: 'max-height 0.3s ease',
                    }}
                >
                    <Typography
                        variant="body2"
                        component="div"
                        sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.7,
                            // Native markdown styling
                            '& p': { mt: 0, mb: 1.5, '&:last-child': { mb: 0 } },
                            '& strong': { fontWeight: 700 },
                            '& ul, & ol': { mt: 0, mb: 1.5, pl: 3 },
                            '& li': { mb: 0.5 },
                            '& code': { 
                                bgcolor: alpha(theme.palette.text.primary, 0.1), 
                                px: 0.6, py: 0.2, 
                                borderRadius: 1, 
                                fontFamily: 'monospace',
                                fontSize: '0.85em'
                            },
                            '& pre': {
                                bgcolor: alpha(theme.palette.text.primary, 0.05),
                                p: 1.5,
                                borderRadius: 2,
                                overflowX: 'auto',
                                '& code': { bgcolor: 'transparent', p: 0 }
                            },
                            '& a': {
                                color: msg.isBot
                                     ? theme.palette.success.dark
                                     : theme.palette.primary.dark,
                                textDecoration: 'underline',
                                fontWeight: 600,
                                '&:hover': {
                                    color: msg.isBot
                                         ? theme.palette.success.main
                                         : theme.palette.primary.main,
                                },
                            },
                        }}
                    >
                        <ReactMarkdown>{stripInternalKeys(msg.text)}</ReactMarkdown>
                    </Typography>
                    
                    {/* Display attachments */}
                    {attachmentUrls.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                mt: 2,
                            }}
                        >
                            {attachmentUrls.map((attachment, index) => (
                                <Chip
                                    key={index}
                                    icon={<ImageIcon />}
                                    label={`Image ${index + 1}`}
                                    onClick={() => handleImageClick(attachment.url)}
                                    clickable
                                    sx={{
                                        bgcolor: alpha(
                                            msg.isBot
                                                ? theme.palette.success.main
                                                : theme.palette.primary.main,
                                            0.15
                                        ),
                                        border: `1px solid ${alpha(
                                            msg.isBot
                                                ? theme.palette.success.main
                                                : theme.palette.primary.main,
                                            0.3
                                        )}`,
                                        color: msg.isBot
                                            ? theme.palette.success.dark
                                            : theme.palette.primary.dark,
                                        fontWeight: 600,
                                        '&:hover': {
                                            bgcolor: alpha(
                                                msg.isBot
                                                    ? theme.palette.success.main
                                                    : theme.palette.primary.main,
                                                0.25
                                            ),
                                            borderColor: msg.isBot
                                                ? theme.palette.success.main
                                                : theme.palette.primary.main,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                    
                    {/* Action Buttons for completion questions */}
                    {isCompletion && suggestedActions.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                mt: 2,
                                pt: 2,
                                borderTop: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            }}
                        >
                            {suggestedActions.map((action, index) => {
                                const isClicked = clickedAction === action;
                                const isDisabled = clickedAction !== null;
                                
                                return (
                                    <Button
                                        key={index}
                                        variant={isClicked ? "contained" : "outlined"}
                                        size="medium"
                                        onClick={() => handleActionClick(action)}
                                        disabled={isDisabled && !isClicked}
                                        sx={{
                                            textTransform: 'none',
                                            borderColor: isClicked 
                                                ? theme.palette.success.main 
                                                : alpha(theme.palette.success.main, 0.5),
                                            bgcolor: isClicked 
                                                ? theme.palette.success.main 
                                                : 'transparent',
                                            color: isClicked 
                                                ? theme.palette.success.contrastText 
                                                : theme.palette.success.dark,
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            px: 2,
                                            py: 1,
                                            '&:hover': {
                                                borderColor: theme.palette.success.main,
                                                bgcolor: isClicked 
                                                    ? theme.palette.success.dark 
                                                    : alpha(theme.palette.success.main, 0.1),
                                            },
                                            '&.Mui-disabled': {
                                                opacity: 0.5,
                                            },
                                        }}
                                    >
                                        {action}
                                    </Button>
                                );
                            })}
                        </Box>
                    )}
                </Box>

                {/* Image Viewer Modal */}
                <ImageViewer
                    open={imageViewerOpen}
                    onClose={() => setImageViewerOpen(false)}
                    imageUrl={selectedImage}
                    altText={`Attachment from ${msg.isBot ? 'Bot' : 'User'}`}
                />
                
                {/* Show All / Show Less Button */}
                {needsExpansion && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 1,
                            px: 2,
                            bgcolor: msg.isBot
                                ? alpha(theme.palette.success.light, 0.15)
                                : alpha(theme.palette.primary.light, 0.15),
                            borderTop: `1px solid ${alpha(
                                msg.isBot
                                    ? theme.palette.success.main
                                    : theme.palette.primary.main,
                                0.2
                            )}`,
                        }}
                    >
                        <Button
                            size="small"
                            onClick={() => setIsExpanded(!isExpanded)}
                            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                                textTransform: 'none',
                                color: msg.isBot
                                    ? theme.palette.success.dark
                                    : theme.palette.primary.dark,
                                fontWeight: 600,
                                '&:hover': {
                                    bgcolor: alpha(
                                        msg.isBot
                                            ? theme.palette.success.main
                                            : theme.palette.primary.main,
                                        0.15
                                    ),
                                },
                            }}
                        >
                            {isExpanded ? 'Show Less' : 'Show All'}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

function ReportCtaCard({
    sessionId,
    isCustomerTemplate,
    reportKind,
}: {
    sessionId: string;
    isCustomerTemplate?: boolean;
    /** Short noun supplied per template (e.g. "Installation", "Tuning").
     *  When present, the card title and button label include it so a
     *  tuning session reads "Tuning documentation ready" / "View Tuning
     *  Report" instead of generic wording. */
    reportKind?: string;
}) {
    const theme = useTheme();
    const navigate = useNavigate();

    // Two flavours of card. Per-customer engagement templates (install,
    // tuning, ...) get an engagement-specific framing; everything else
    // gets a neutral technical-session-report framing. The `reportKind`
    // noun (when present) personalises the engagement copy per template
    // so a tuning session doesn't read as "Installation".
    const kind = (reportKind || '').trim();
    const title = isCustomerTemplate
        ? kind
            ? `${kind} documentation ready`
            : 'Documentation ready'
        : 'Session report ready';
    const description = isCustomerTemplate
        ? `We compiled this session into a structured customer ${kind.toLowerCase() ? `${kind.toLowerCase()} ` : ''}document with the target environment, prerequisites, step-by-step procedure, validation checks, troubleshooting, references, and a sign-off block. Open it to review, print, or export.`
        : 'We compiled this session into a structured technical report covering what was done, what was verified, references used, items requiring manual review, and a sign-off block. Open it to review, print, or export.';
    const buttonLabel = isCustomerTemplate
        ? kind
            ? `View ${kind} Report`
            : 'View Report'
        : 'View Session Report';

    return (
        <Box sx={{ alignSelf: 'stretch', maxWidth: '720px' }}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '12px',
                    p: 3,
                    bgcolor: alpha(theme.palette.success.light, 0.12),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.success.main, 0.18),
                        color: theme.palette.success.dark,
                    }}
                >
                    <DescriptionIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.text.primary }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                        {description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            color="success"
                            size="medium"
                            startIcon={<DescriptionIcon />}
                            onClick={() => navigate(`/sessions/${sessionId}/report`)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                        >
                            {buttonLabel}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}

function KnownIssueCard({ matches }: { matches: KnownIssueMatch[] }) {
    const theme = useTheme();
    return (
        <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '12px',
                    bgcolor: alpha(theme.palette.warning.light, 0.18),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                    p: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <WarningAmberIcon sx={{ color: theme.palette.warning.main }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.warning.dark }}>
                        {matches.length === 1
                            ? 'Known issue detected in your output'
                            : `${matches.length} known issues detected in your output`}
                    </Typography>
                </Box>
                {matches.map((m, idx) => (
                    <KnownIssueRow key={m.issue_id || idx} match={m} />
                ))}
            </Paper>
        </Box>
    );
}

function KnownIssueRow({ match }: { match: KnownIssueMatch }) {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    return (
        <Box
            sx={{
                mt: 1,
                p: 1.25,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setExpanded(v => !v)}
            >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {match.issue || 'Unnamed issue'}
                </Typography>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ mt: 0.75, fontSize: '0.85rem', color: theme.palette.text.primary }}>
                    {match.cause && (
                        <Box sx={{ mb: 0.5 }}>
                            <strong>Cause:</strong> {match.cause}
                        </Box>
                    )}
                    {match.resolution && (
                        <Box sx={{ mb: 0.5 }}>
                            <strong>Resolution:</strong> {match.resolution}
                        </Box>
                    )}
                    {match.log_locations && match.log_locations.length > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                            <strong>Logs:</strong> {match.log_locations.slice(0, 3).join(' · ')}
                        </Box>
                    )}
                    {match.related_procedure && (
                        <Box sx={{ mb: 0.5 }}>
                            <strong>Related procedure:</strong> {match.related_procedure}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

export function ChatMessages({ messages, onSendReply, highlightedSourceTimestamp }: ChatMessagesProps) {
    if (messages.length === 0) return null;

    return (
        <>
            {messages.map((msg) => (
                <CollapsibleMessage
                    key={msg.id}
                    msg={{
                        ...msg,
                        highlight: Boolean(
                            highlightedSourceTimestamp &&
                            msg.sourceTimestamp &&
                            msg.sourceTimestamp.startsWith(highlightedSourceTimestamp.slice(0, 19))
                        ),
                    }}
                    onSendReply={onSendReply}
                />
            ))}
        </>
    );
}
